/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useMemo} from 'react'
import {Link} from 'react-router-dom'
import clsx from 'clsx'
import {brandName, externalDashboard, isMegaPay} from '../../../_metronic/helpers'
import {PageTitle} from '../../../_metronic/layout/core'
import {CurrentCompany} from '../../modules/apps/invoice-management/invoices-list/components/header/CurrentCompany'
import {AdminCompanySelector} from '../../modules/apps/shared/admin-company-selector/AdminCompanySelector'
import {useAuth} from '../../modules/auth'
import {canUseTicketing} from '../../modules/auth/core/ticketingAccess'
import {isOnPremisesInstallation} from '../../modules/auth/core/onPremisesAccess'
import {DashboardTicketAnnouncement} from './DashboardTicketAnnouncement'
import {DashboardPrintTemplates} from './DashboardPrintTemplates'
import {DashboardReports} from './DashboardReports'
import {SsoDashboardNotices} from './SsoDashboardNotices'
import {CredentialDashboardNotice} from './CredentialDashboardNotice'
import {DashboardInstallNotice} from '../../modules/install-app'
import {InvoiceSeasonDashboardSummary} from './InvoiceSeasonDashboardSummary'
import {SendHealthPanel} from '../../modules/apps/monitoring/send-health/SendHealthPanel'
import {InvoiceTypeFavDashboard} from './InvoiceTypeFavDashboard'
import {DashboardActivitySection} from './activity/DashboardActivitySection'
import {getStuffIdSearchUrl} from '../../modules/apps/stuffid-management/stuffidSite'
import './DashboardWrapper.scss'
import {staffCan, staffPicksBusiness} from '../../modules/auth/core/staffAccess'

type ActionLink = {
  title: string
  caption: string
  icon: string
  tone: 'blue' | 'green' | 'amber' | 'red' | 'slate'
  to?: string
  href?: string
  disabled?: boolean
  /** Not on the bank's own install. */
  cloudOnly?: boolean
}

const quickLinks: ActionLink[] = [
  {
    title: 'صورتحساب‌های فروش',
    caption: 'لیست، ارسال و پیگیری فروش',
    icon: 'la-file-invoice',
    tone: 'blue',
    to: '/apps/invoice-management/invoices',
  },
  {
    title: 'صورتحساب‌های خرید',
    caption: 'مشاهده و بررسی خریدها',
    icon: 'la-cart-arrow-down',
    tone: 'green',
    to: '/apps/invoice-management/purchases',
    disabled: !isMegaPay,
    // The bank's install has no purchases list (its menu has none either; the user, 2026-10-26).
    cloudOnly: true,
  },
  {
    title: 'خریداران',
    caption: 'اطلاعات مشتری و شناسه‌ها',
    icon: 'la-user-tie',
    tone: 'amber',
    to: '/apps/customer-management/customers',
  },
  {
    title: 'کالا و خدمات',
    caption: 'اقلام، خدمات و قیمت‌ها',
    icon: 'la-dolly',
    tone: 'slate',
    to: '/apps/product-management/products',
  },
  {
    title: 'شناسه کالا',
    caption: 'شناسه‌های عمومی و اختصاصی',
    icon: 'la-search',
    tone: 'blue',
    href: getStuffIdSearchUrl(),
    disabled: !isMegaPay,
    // The bank's install looks IDs up in its forms but has no ID page (the user, 2026-10-25).
    cloudOnly: true,
  },
  {
    title: 'تنظیمات مودی',
    caption: 'پرونده و تنظیمات شرکت',
    icon: 'la-building',
    tone: 'red',
    to: '/apps/taxpayer-management/companies',
  },
]

const DashboardWrapper: FC = () => {
  const {currentUser} = useAuth()
  const ticketingEnabled = canUseTicketing(currentUser)
  // The selected business, as every dashboard section reads it (an administrator's pick included).
  const companyId = Number(currentUser?.cpi || 0)
  const showPrintTemplates = companyId > 0

  // The send health panel is for staff granted «مانیتورینگ و ارسال» in the CRM.
  const isSystemAdmin = staffCan(currentUser, 'monitoring')
  // «شنبه ۲۵ مهر ۱۴۰۵»: put together from its parts, as some browsers write «۱۴۰۵ مهر ۲۵, شنبه» (508).
  const todayLabel = useMemo(() => {
    const parts = new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
      timeZone: 'Asia/Tehran',
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).formatToParts(new Date())
    const part = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((item) => item.type === type)?.value || ''
    return [part('weekday'), part('day'), part('month'), part('year')].filter(Boolean).join(' ')
  }, [])

  const onPremises = isOnPremisesInstallation(currentUser)
  const activeQuickLinks = useMemo(
    () => quickLinks.filter((link) => !link.disabled && !(link.cloudOnly && onPremises)),
    [onPremises]
  )
  const displayName = currentUser?.fn || currentUser?.un || 'کاربر'

  return (
    <>
      <PageTitle breadcrumbs={[]}>داشبورد</PageTitle>

      <div className='mp-dashboard'>
        <section className='mp-dashboard__topbar'>
          <div>
            <span className='mp-dashboard__eyebrow'>میز کار</span>
            <h1>داشبورد {brandName}</h1>
            <p>
              {displayName}، عملیات در جریان، آخرین فعالیت‌ها و خطاهای کسب‌وکار و مسیرهای اصلی صدور
              صورتحساب اینجاست.
            </p>
          </div>

          <div className='mp-dashboard__company'>
            {/* The dashboard follows the selected business; staff pick any business here. */}
            {staffPicksBusiness(currentUser) ? (
              <AdminCompanySelector
                className='mp-dashboard__admin-company'
                placeholder='همه کسب‌وکارها'
              />
            ) : (
              <CurrentCompany
                isChangeable={true}
                comboStyle={{width: 'fit-content', backgroundColor: '#ffffff', marginBottom: '2px'}}
              />
            )}
            <div className='mp-dashboard__date'>
              <i className='las la-calendar-day' />
              <span>{todayLabel}</span>
            </div>
          </div>
        </section>

        <SsoDashboardNotices user={currentUser} />

        <CredentialDashboardNotice user={currentUser} />

        <DashboardInstallNotice />

        {/*
          The administrator's send/inquiry monitor. It replaces the three surfaces that were
          removed from the panel, and it is the only place that shows the «استعلام نا مشخص»
          bucket — invoices in phase 3 with no tax status, which belonged to none of the old
          success/failed/in-progress counters and were therefore invisible.
        */}
        {isSystemAdmin && <SendHealthPanel />}

        {/*
          The selected business's work in progress, its latest registrations grouped into
          batches with their send status, the errors to look at and the goods/services invoiced
          last — each linking to the invoice list already filtered to those invoices.
        */}
        <DashboardActivitySection />

        <InvoiceSeasonDashboardSummary />

        {/* «گزارش‌ها»: an introduction to the periodic reports until the business makes one, then its
            latest runs; every state leads to the reports page. */}
        <DashboardReports companyId={companyId} />

        {/* Side by side and of one height: the links sit in a grid so neither box leaves a tall
            empty column next to the other. A second row keeps the same two columns: the print
            templates under the links, the support box under the new invoice. */}
        <section
          className={clsx('mp-dashboard__workspace', {
            'mp-dashboard__workspace--paired': showPrintTemplates && ticketingEnabled,
          })}
        >
          <section className='mp-panel mp-panel--quick' aria-labelledby='mp-quick-invoice-title'>
            <div className='mp-panel__head'>
              <div>
                <span>صدور صورتحساب</span>
                <h2 id='mp-quick-invoice-title'>صورتحساب جدید</h2>
              </div>
              <i className='las la-plus-circle' aria-hidden='true' />
            </div>
            <InvoiceTypeFavDashboard />
          </section>

          <section className='mp-panel mp-panel--links' aria-labelledby='mp-quick-links-title'>
            <div className='mp-panel__head'>
              <div>
                <span>لینک‌های کاری</span>
                <h2 id='mp-quick-links-title'>دسترسی سریع</h2>
              </div>
              <i className='las la-th-large' aria-hidden='true' />
            </div>

            <div className='mp-action-list'>
              {activeQuickLinks.map((link) => {
                const content = (
                  <>
                    <span className={`mp-action-list__icon mp-action-list__icon--${link.tone}`}>
                      <i className={`las ${link.icon}`} aria-hidden='true' />
                    </span>
                    <span>
                      <strong>{link.title}</strong>
                      <small>{link.caption}</small>
                    </span>
                    <i className='las la-angle-left' aria-hidden='true' />
                  </>
                )

                return link.href ? (
                  <a
                    key={link.title}
                    href={link.href}
                    target='_blank'
                    rel='noreferrer'
                    className='mp-action-list__item'
                  >
                    {content}
                  </a>
                ) : (
                  <Link key={link.title} to={link.to || '#'} className='mp-action-list__item'>
                    {content}
                  </Link>
                )
              })}
            </div>
          </section>

          {showPrintTemplates && <DashboardPrintTemplates companyId={companyId} />}
          {ticketingEnabled && <DashboardTicketAnnouncement compact />}
        </section>
      </div>
    </>
  )
}

export {DashboardWrapper}
